import {
  BrainCircuit,
  BookOpenText,
  Briefcase,
  Cpu,
  CircuitBoard,
  Building2,
  Microscope,
  Newspaper,
  Palette,
  ScanSearch,
  Users,
  Headphones,
  Sparkles,
  Workflow,
  Zap,
} from "lucide-react";
import { DEFAULT_DESCRIPTION } from "../../layouts/seoMetadata";
import { APP_STORE_URL, GOOGLE_PLAY_URL, STORE_LINKS } from "../../config/storeLinks";

export const LIGHT_PAGES = new Set(["/privacy", "/terms", "/cookie", "/refund-policy"]);

// Pages whose top section is a full-bleed photo.
export const PHOTO_HERO_PAGES = new Set([
  "/",
  "/software",
  "/ax",
  // TODO: Restore Developers navigation after the developer surface ships.
  "/research",
  "/company",
]);

export const NAV_ITEMS = [
  {
    label: "Software",
    path: "/software",
    matchPaths: ["/software", "/products", "/product"],
    subnavKey: "software",
  },
  {
    label: "Ateno AX",
    path: "/ax",
    matchPaths: ["/ax", "/products/ax", "/products/hardware"],
    subnavKey: "ax",
  },
  {
    label: "Research",
    path: "/research",
    matchPaths: ["/research"],
    subnavKey: "research",
  },
  {
    label: "Company",
    path: "/company",
    matchPaths: ["/company", "/about", "/careers", "/support", "/team", "/news", "/blog"],
    subnavKey: "company",
  },
];

export const SECTION_SUBNAV = {
  software: {
    label: "Software",
    icon: Sparkles,
    variant: "product",
    eyebrow: "ATENO STUDIO",
    title: "Redesign your room with AI.",
    description: "Scan or upload your space, generate a new look, refine the details, and discover furniture that fits.",
    storeLinks: STORE_LINKS,
    items: [
      {
        label: "How it works",
        subtitle: "Scan, generate, refine, and visualize.",
        to: "/software#workflow",
        icon: Workflow,
      },
      {
        label: "Explore designs",
        subtitle: "Browse room transformations, styles, and ideas.",
        to: "/software#app-screens",
        icon: ScanSearch,
      },
      {
        label: "Style library",
        subtitle: "Room types, palettes, budgets, and design styles.",
        to: "/software#style-library",
        icon: Palette,
      },
    ],
  },
  ax: {
    label: "Ateno AX",
    icon: Cpu,
    preview: "A coprocessor for 3D Gaussian Splatting. Currently an FPGA prototype.",
    items: [
      { label: "Product status", to: "/ax#architecture", icon: Cpu },
      { label: "Acceleration", to: "/ax#acceleration", icon: Zap },
      { label: "Applications", to: "/ax#applications", icon: CircuitBoard },
    ],
  },
  research: {
    label: "Research",
    icon: Microscope,
    preview: "Technical work on spatial intelligence, reconstruction systems, and hardware acceleration.",
    items: [
      { label: "Spatial Intelligence", to: "/research#spatial-intelligence", icon: BrainCircuit },
      { label: "Reconstruction", to: "/research#reconstruction", icon: ScanSearch },
      { label: "Hardware Research", to: "/research#hardware-research", icon: Microscope },
      { label: "Published Work", to: "/research#papers", icon: BookOpenText },
      { label: "Publications", to: "/research#publications", icon: Newspaper },
    ],
  },
  company: {
    label: "Company",
    icon: Building2,
    preview: "The team, mission, and support surfaces behind Ateno's spatial computing platform.",
    items: [
      { label: "About", to: "/company", icon: Building2 },
      { label: "Team", to: "/company#team", icon: Users },
      { label: "Newsroom", to: "/company/newsroom", icon: Newspaper },
      { label: "Careers", to: "/careers", icon: Briefcase },
      { label: "Support", to: "/support", icon: Headphones },
    ],
  },
};

function pathnameMatches(pathname, path) {
  return pathname === path || pathname.startsWith(`${path}/`);
}

export function getPrimaryNavItem(pathname) {
  let bestItem = null;
  let bestSpecificity = -1;

  NAV_ITEMS.forEach((item) => {
    const matches = [item.path, ...(item.matchPaths || [])];
    matches.forEach((path) => {
      if (!pathnameMatches(pathname, path)) {
        return;
      }

      const specificity = path.length;
      if (specificity > bestSpecificity) {
        bestSpecificity = specificity;
        bestItem = item;
      }
    });
  });

  return bestItem;
}

export function getSectionSubnav(pathname) {
  const item = getPrimaryNavItem(pathname);
  if (!item?.subnavKey) {
    return null;
  }
  return SECTION_SUBNAV[item.subnavKey] || null;
}

export const FOOTER_GROUPS = [
  {
    title: "Software",
    items: [
      ["Ateno Studio", "/software"],
      ["Pricing"],
      ["App Store", APP_STORE_URL],
      ["Google Play", GOOGLE_PLAY_URL],
      ["Product Demo", "https://product-demo.ateno.co"],
    ],
  },
  {
    title: "Ateno AX",
    items: [
      ["Architecture", "/ax"],
    ],
  },
  {
    title: "Research",
    items: [
      ["Research", "/research"],
      ["Technical Reports", "/research"],
      ["Publications", "/research"],
    ],
  },
  {
    title: "Company",
    items: [
      ["About", "/company"],
      ["Newsroom", "/company/newsroom"],
      ["Careers", "/careers"],
      ["Support", "/support"],
    ],
  },
  {
    title: "Legal",
    items: [
      ["Privacy", "/privacy"],
      ["Terms", "/terms"],
      ["Cookie Policy", "/cookie"],
      ["Refund Policy", "/refund-policy"],
      ["Security", "/security"],
    ],
  },
];

export const FOOTER_COPY = DEFAULT_DESCRIPTION;

export function getSectionIsDark() {
  const navY = window.innerWidth <= 768 ? 58 : 116;
  let best = null;
  let bestTop = -Infinity;

  document.querySelectorAll("section").forEach((section) => {
    const rect = section.getBoundingClientRect();
    if (rect.top <= navY && rect.bottom >= navY && rect.top > bestTop) {
      bestTop = rect.top;
      best = section;
    }
  });

  const theme = document.documentElement.getAttribute("data-theme") || "dark";
  const isLightTheme = theme === "light";

  if (best?.classList.contains("home-hero-dark-nav")) {
    return false;
  }

  if (best?.classList.contains("photo-section")) {
    return true;
  }

  if (isLightTheme) {
    return false;
  }

  return !best?.classList.contains("section-light");
}
